import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiShieldLockOutline } from '@mdi/js'
import { AuthLayout } from '../components/auth/AuthLayout'
import { MfaChallengeForm } from '../components/auth/MfaChallengeForm'
import { AppIcon } from '../components/ui/icon'
import { useAuth } from '../hooks/useAuth'
import {
    MfaApiError,
    mfaErrorKey,
    PENDING_CHALLENGE_KEY,
    stepUpChallenge,
    type LoginResponse,
    type PendingChallenge,
} from '../lib/mfa'
import { safeNextPath } from '../lib/safeNextPath'
import { fetchProviders } from '../lib/passkeys'

interface MfaVerifySearch {
    org?: string
    next?: string
}

// The step-up page (spec 2026-10-01-03): the current session was never
// verified and either its refresh found the organization now requires
// two-step verification (the challenge waits in sessionStorage), or it
// entered another organization that does (403 mfa_required: a step-up
// challenge is requested here). Verifying upgrades the same session.
export const Route = createFileRoute('/mfa-verify')({
    component: MfaVerifyPage,
    validateSearch: (search: Record<string, unknown>): MfaVerifySearch => ({
        org: (search.org as string) || undefined,
        next: (search.next as string) || undefined,
    }),
})

function readPendingChallenge(): PendingChallenge | null {
    try {
        const raw = sessionStorage.getItem(PENDING_CHALLENGE_KEY)
        return raw ? (JSON.parse(raw) as PendingChallenge) : null
    } catch {
        return null
    }
}

function MfaVerifyPage() {
    const { t } = useTranslation('auth')
    const navigate = useNavigate()
    const { org: orgParam, next } = Route.useSearch()
    const { isAuthenticated, orgCode, logout, applyUpgradedAccessToken } = useAuth()
    const org = orgParam || orgCode || 'demo'
    const [challenge, setChallenge] = useState<PendingChallenge | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [passkeysEnabled, setPasskeysEnabled] = useState(false)

    useEffect(() => {
        fetchProviders()
            .then((res) => setPasskeysEnabled(!!res.passkeys_enabled))
            .catch(() => { /* the passkey entry then says passkeys are unavailable */ })
    }, [])

    useEffect(() => {
        if (!isAuthenticated) {
            navigate({ to: '/$org/login', params: { org }, search: { next: next || undefined, email: undefined, password: undefined } })
            return
        }
        const pending = readPendingChallenge()
        if (pending) {
            setChallenge(pending)
            return
        }
        stepUpChallenge(org)
            .then(setChallenge)
            .catch((err) => {
                if (err instanceof MfaApiError && err.code === 'mfa_enrollment_required') {
                    navigate({ to: '/mfa-setup', search: { org, next }, replace: true })
                    return
                }
                setError(t(mfaErrorKey(err instanceof MfaApiError ? err.code : null)))
            })
    }, [isAuthenticated, navigate, org, next, t])

    const onVerified = (resp: LoginResponse) => {
        if (resp.access_token) {
            applyUpgradedAccessToken(resp.access_token, resp.expires_in ?? 3600)
        }
        const target = safeNextPath(next)
        if (target && !target.startsWith('/mfa-')) {
            window.location.assign(target)
            return
        }
        navigate({ to: '/$org', params: { org } })
    }

    return (
        // The bare shell has no flex column for AuthLayout's flex-1 to fill.
        <div className="flex min-h-svh flex-col">
        <AuthLayout>
            <div className="w-full max-w-sm rounded-2xl border bg-card p-8 shadow-dialog" data-testid="mfa-verify-page">
                <div className="flex items-center gap-3">
                    <AppIcon path={mdiShieldLockOutline} size="lg" className="text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">{t('mfa.stepup.title')}</h2>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{t('mfa.stepup.description')}</p>
                <div className="mt-4">
                    {error ? (
                        <p className="text-sm text-destructive" data-testid="mfa-verify-error">{error}</p>
                    ) : challenge ? (
                        <MfaChallengeForm
                            org={org}
                            challenge={challenge}
                            onVerified={onVerified}
                            onCancel={() => logout(org)}
                            cancelLabel={t('mfa.setup.sign_out')}
                            passkeysEnabled={passkeysEnabled}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground">{t('mfa.stepup.loading')}</p>
                    )}
                </div>
            </div>
        </AuthLayout>
        </div>
    )
}
